/**
 * Marginal cost of one subscriber: Cloudflare, the included AI credit, and
 * Stripe on the yearly charge.
 *
 * Change the rates and the usage profiles here. The pricing page reads
 * cloudflareCostLines() and does not duplicate the arithmetic.
 *
 * Rooms hibernate. Cloudflare bills an incoming WebSocket message as a
 * Durable Object request, then divides those messages by 20. Outgoing
 * broadcasts are not requests. Handler time is billed on the way in.
 * Included Workers Paid allotments are not subtracted.
 *
 * Cloudflare Workers Paid, public rates, USD:
 *   Durable Object requests     $0.15 / million
 *   Durable Object duration     $12.50 / million GB-s, at 128 MB
 *   SQLite stored data          $0.20 / GB-month
 *   SQLite rows read            $0.001 / million
 *   SQLite rows written         $1.00 / million
 *   Worker requests             $0.30 / million
 *   Worker CPU                  $0.02 / million CPU-ms
 *   R2 storage                  $0.015 / GB-month
 *   R2 Class A writes           $4.50 / million
 *   R2 Class B reads            $0.36 / million
 *
 * Editing hours are per person per month, not per font. A collab message is a committed Yjs update, not a pointer
 * frame. Outline drags mutate locally and sync once on mouse-up
 * (`OutlineEditor._syncCurrentGlyphToYDoc`). Canvas arrow-key nudges and text
 * kerning preview commit once, 1 second after the last key
 * (`KEYBOARD_PREVIEW_COMMIT_DEBOUNCE`). `messagesPerEditHour` is that commit
 * rate. A room checkpoints at most every 30 minutes. Each checkpoint compacts
 * and validates the active shard and the font-core shard.
 *
 * Glyph and core bytes are measured compact JSON, not rounded estimates.
 * Medians are `median_glyph_bytes` from cjk-collab-sizing. A font core is
 * one measured total, not a median. The gfsources library histogram bins
 * glyphs at 10 KB, so it has no exact median to use here.
 * Shards are capped at 5 MB. Opening a font reads every shard.
 *
 * Stripe Germany, on the VAT-inclusive charge (German VAT 19%). A US charge
 * has no VAT, so this overstates that case. VAT on Stripe's own fee is
 * input tax and is not added.
 *   Premium EEA card     2.8% + €0.25 (standard EEA is 1.5% + €0.25)
 *   Stripe Tax Basic     0.5% where tax is collected
 *   Stripe Billing       0.7% of subscription volume
 *   Stripe Invoicing     0.4% of a paid one-time invoice
 * Shares of the plan, and of a 160-hour month, are `USAGE_INTENSITY_PERCENT`.
 * Stripe Invoicing at 0.4% applies only to usage past the
 * allowance, which these three profiles do not reach.
 */

import { yearlyMonths } from "./pricing-config.js";

export const CLOUDFLARE_RATES_USD = {
  doRequestPerMillion: 0.15,
  doDurationPerMillionGbSeconds: 12.5,
  doBilledMemoryGb: 0.128,
  activeSecondsPerMessage: 0.05,
  sqliteStoragePerGbMonth: 0.2,
  sqliteRowReadPerMillion: 0.001,
  sqliteRowWritePerMillion: 1,
  sqliteRowsReadPerMessage: 6,
  /**
   * Rows written for one committed update: spool delete, spool insert,
   * spool status, room log, room state, and the write receipt.
   */
  sqliteRowsWrittenPerMessage: 6,
  workerRequestPerMillion: 0.3,
  workerCpuPerMillionMs: 0.02,
  r2StoragePerGbMonth: 0.015,
  r2ClassAPerMillion: 4.5,
  r2ClassBPerMillion: 0.36,
  usdToEur: 0.92,
};

/** Stripe Germany. One charge per yearly plan. */
export const STRIPE_CARD_FEE = {
  /** Premium EEA card. Standard EEA cards are 1.5%. */
  percent: 0.028,
  fixedEur: 0.25,
  /** Stripe Tax Basic, on Checkout / Billing / Invoicing. */
  taxPercent: 0.005,
  /** Stripe Billing, pay as you go, on subscription volume. */
  billingPercent: 0.007,
  /** Stripe Invoicing, per paid one-time invoice. Yearly overage uses this. */
  invoicingPercent: 0.004,
  /** German VAT. Percentage fees apply to the gross charge. */
  chargeVatRate: 0.19,
};

/** A full editing month. Profile hours are a share of this. */
export const FULL_MONTH_HOURS = 160;
/** Incoming WebSocket messages are billed as one request per 20 messages. */
const WEBSOCKET_MESSAGE_BILLING_RATIO = 20;

/**
 * Usage intensity for the pricing-page table and the cost model.
 * Each number is a percent of the plan allowance, or of a 160-hour month.
 * Edit the numbers here. `hours` rows are that percent of FULL_MONTH_HOURS.
 * Byte sizes and commits per hour stay on the profiles below.
 */
export const USAGE_INTENSITY_PERCENT = [
  { label: "Fonts used", key: "fontFill", light: 80, medium: 80, heavy: 80 },
  { label: "Glyphs filled", key: "glyphFill", light: 80, medium: 80, heavy: 80 },
  { label: "Invitees active", key: "inviteFill", light: 20, medium: 50, heavy: 80 },
  { label: "Invitee seats connected", key: "seatPresence", light: 20, medium: 50, heavy: 80 },
  { label: "Owner time", key: "ownerHours", hours: true, light: 20, medium: 50, heavy: 80 },
  { label: "Collaborator time", key: "hoursPerInvitee", hours: true, light: 20, medium: 50, heavy: 80 },
  { label: "AI credit spent", key: "creditUse", light: 20, medium: 50, heavy: 80 },
];

const USAGE_INTENSITIES = ["light", "medium", "heavy"];

function intensityValue(row, intensity) {
  const fraction = row[intensity] / 100;
  return row.hours ? fraction * FULL_MONTH_HOURS : fraction;
}

function usageProfile(id, label, measured) {
  const profile = { label, ...measured };
  for (const row of USAGE_INTENSITY_PERCENT) {
    profile[row.key] = intensityValue(row, id);
  }
  return profile;
}

/**
 * Byte sizes and commits per hour are measurements, not shares of the plan.
 * The share fields are filled from USAGE_INTENSITY_PERCENT.
 *
 * Glyph medians and cores, compact UTF-8 JSON after expanding path nodes:
 * - Light glyph: Plangothic P1 median, 1,567 bytes. P2's median is 1,309.
 *   Core: Roboto Flex font core, 2,050,740 bytes.
 *   cjk-collab-sizing/results/sizing.json (PlangothicP1-Regular)
 *   cjk-collab-sizing/results/roboto_flex_sizing.json
 * - Medium glyph: Source Han Sans SC VF median, 3,368 bytes.
 *   Core: Plangothic P1 core, 6,458,734 bytes.
 *   cjk-collab-sizing/results/sizing.json
 * - Heavy glyph: Roboto Flex median, 26,269 bytes. The mean is 67,110 and is
 *   not used. Core: Source Han Sans SC VF core, 139,952,884 bytes, of which
 *   features.prefixes are about 128.8 MB (cjk-collab-sizing/results/sizing.md).
 */
export const CLOUDFLARE_USAGE_PROFILES = {
  light: usageProfile("light", "Light", {
    messagesPerEditHour: 400,
    opensPerPerson: 8,
    storedBytesPerGlyph: 1567,
    coreBytesPerFont: 2050740,
  }),
  medium: usageProfile("medium", "Medium", {
    messagesPerEditHour: 800,
    opensPerPerson: 20,
    storedBytesPerGlyph: 3368,
    coreBytesPerFont: 6458734,
  }),
  heavy: usageProfile("heavy", "Heavy", {
    messagesPerEditHour: 1500,
    opensPerPerson: 40,
    storedBytesPerGlyph: 26269,
    coreBytesPerFont: 139952884,
  }),
};

/** Shares of the plan, or of a 160-hour month, for the pricing page. */
export function usageAssumptionRows() {
  return USAGE_INTENSITY_PERCENT.map((row) => ({
    label: row.label,
    values: USAGE_INTENSITIES.map((intensity) => row[intensity] / 100),
  }));
}

const SQLITE_SHARE_OF_CHECKPOINT = 0.2;
/** A room checkpoints at most every 30 minutes. */
const CHECKPOINT_WRITES_PER_EDIT_HOUR = 2;
/** Active glyph shard plus the font-core shard. */
const SHARDS_TOUCHED_PER_CHECKPOINT = 2;
const COMPACTION_CPU_MS = 200;
const VALIDATION_CPU_MS = 100;
/** Encoded shard ceiling in the collab protocol. */
const MAX_SHARD_BYTES = 5 * 1024 * 1024;
const SERVICE_MONTHS_PER_YEAR = 12;

/**
 * Heat-map band for a profit margin. Null when there is no revenue.
 * Below 0 is red, below 25% is yellow, below 50% is green.
 */
export function marginBand(margin) {
  if (margin == null || !Number.isFinite(margin)) return null;
  if (margin < 0) return "red";
  if (margin < 0.25) return "yellow";
  if (margin < 0.5) return "green";
  return null;
}

export function monthlyIncludedAiCreditEur(product, monthlyExclusiveEur) {
  const fixed = Number(product?.freeCredits) || 0;
  if (fixed > 0) return fixed;
  return (Number(monthlyExclusiveEur) || 0) * (Number(product?.creditShare) || 0);
}

/**
 * Card fee, Stripe Tax, and Stripe Billing on the yearly subscription,
 * plus Stripe Invoicing on `yearlyOverageEur`. Amounts are exclusive euros.
 * Percentages apply after German VAT. No subscription charge, no fee.
 */
export function stripeFeeEur(yearlyRevenueEur, yearlyOverageEur = 0) {
  if (!(yearlyRevenueEur > 0)) return 0;
  const vat = 1 + STRIPE_CARD_FEE.chargeVatRate;
  const subscriptionGross = yearlyRevenueEur * vat;
  const overageGross = Math.max(0, Number(yearlyOverageEur) || 0) * vat;
  const cardAndTax = subscriptionGross * (STRIPE_CARD_FEE.percent + STRIPE_CARD_FEE.taxPercent)
    + STRIPE_CARD_FEE.fixedEur;
  const billing = subscriptionGross * STRIPE_CARD_FEE.billingPercent;
  const invoicing = overageGross * STRIPE_CARD_FEE.invoicingPercent;
  return cardAndTax + billing + invoicing;
}

function inboundMessages(hours, profile) {
  return hours * profile.messagesPerEditHour;
}

function messageCostUsd(inbound, rates) {
  const requests = (inbound / WEBSOCKET_MESSAGE_BILLING_RATIO * rates.doRequestPerMillion) / 1e6;
  const gbSeconds = inbound * rates.activeSecondsPerMessage * rates.doBilledMemoryGb;
  const duration = (gbSeconds * rates.doDurationPerMillionGbSeconds) / 1e6;
  const rowWrites = inbound * rates.sqliteRowsWrittenPerMessage;
  const rowReads = inbound * rates.sqliteRowsReadPerMessage;
  const rows = (rowWrites * rates.sqliteRowWritePerMillion) / 1e6
    + (rowReads * rates.sqliteRowReadPerMillion) / 1e6;
  return requests + duration + rows;
}

function checkpointCostUsd(hours, rates) {
  const checkpoints = hours * CHECKPOINT_WRITES_PER_EDIT_HOUR;
  const shards = checkpoints * SHARDS_TOUCHED_PER_CHECKPOINT;
  const workerCalls = shards * 2;
  const cpuMs = shards * (COMPACTION_CPU_MS + VALIDATION_CPU_MS);
  return (workerCalls * rates.workerRequestPerMillion) / 1e6
    + (cpuMs * rates.workerCpuPerMillionMs) / 1e6
    + (shards * rates.r2ClassAPerMillion) / 1e6;
}

function openCostUsd(opens, shardCount, rates) {
  const reads = opens * shardCount;
  return (reads * rates.r2ClassBPerMillion) / 1e6
    + (opens * rates.workerRequestPerMillion) / 1e6;
}

function emptyCost() {
  return { usd: 0, eur: 0, glyphs: 0, fonts: 0, invitees: 0 };
}

/**
 * Monthly Cloudflare cost of one plan at one usage profile.
 *
 * @param {{ maxFontsOwned?: number, maxGlyphsPerFont?: number, maxInvitesPerAsset?: number, collaboration?: boolean }} product
 * @param {keyof typeof CLOUDFLARE_USAGE_PROFILES} profileId
 */
export function estimateCloudflarePlanCost(product, profileId, rates = CLOUDFLARE_RATES_USD) {
  const profile = CLOUDFLARE_USAGE_PROFILES[profileId];
  if (!profile || product?.collaboration === false) return emptyCost();
  const fonts = (Number(product?.maxFontsOwned) || 0) * profile.fontFill;
  if (!fonts) return emptyCost();

  const glyphsPerFont = (Number(product.maxGlyphsPerFont) || 0) * profile.glyphFill;
  const invitees = (Number(product.maxInvitesPerAsset) || 0) * profile.inviteFill;
  const bytesPerFont = glyphsPerFont * profile.storedBytesPerGlyph + profile.coreBytesPerFont;
  const storedBytes = fonts * bytesPerFont;
  const r2Gb = storedBytes / 1e9;
  const sqliteGb = r2Gb * SQLITE_SHARE_OF_CHECKPOINT;
  const glyphUsd = r2Gb * rates.r2StoragePerGbMonth + sqliteGb * rates.sqliteStoragePerGbMonth;
  const shardCount = Math.max(1, Math.ceil(bytesPerFont / MAX_SHARD_BYTES));

  const ownerInbound = inboundMessages(profile.ownerHours, profile);
  const inviteeInbound = inboundMessages(invitees * profile.hoursPerInvitee, profile);
  const fontUsd = messageCostUsd(ownerInbound, rates)
    + checkpointCostUsd(profile.ownerHours, rates)
    + openCostUsd(profile.opensPerPerson, shardCount, rates);
  const inviteeUsd = messageCostUsd(inviteeInbound, rates)
    + checkpointCostUsd(invitees * profile.hoursPerInvitee, rates)
    + openCostUsd(invitees * profile.opensPerPerson, shardCount, rates);
  const usd = glyphUsd + fontUsd + inviteeUsd;

  return {
    usd,
    eur: usd * rates.usdToEur,
    glyphs: glyphUsd * rates.usdToEur,
    fonts: fontUsd * rates.usdToEur,
    invitees: inviteeUsd * rates.usdToEur,
  };
}

/**
 * Yearly cost. Cloudflare is twelve months of the profile. Included AI
 * credit is twelve months of the allowance times that profile's creditUse.
 * Usage past the allowance is billed as yearly-plan overage and picks up
 * the Stripe Invoicing fee. Revenue is `yearlyMonths` times the monthly
 * exclusive price.
 */
export function cloudflareCostLines(product, monthlyExclusiveEur) {
  const revenueEur = monthlyExclusiveEur * yearlyMonths;
  const yearlyAllowanceEur = monthlyIncludedAiCreditEur(product, monthlyExclusiveEur) * SERVICE_MONTHS_PER_YEAR;
  return Object.keys(CLOUDFLARE_USAGE_PROFILES).map((profileId) => {
    const profile = CLOUDFLARE_USAGE_PROFILES[profileId];
    const yearlyAiEur = yearlyAllowanceEur * profile.creditUse;
    const overageEur = Math.max(0, yearlyAiEur - yearlyAllowanceEur);
    const yearlyStripeEur = stripeFeeEur(revenueEur, product?.creditOverage ? overageEur : 0);
    const monthly = estimateCloudflarePlanCost(product, profileId);
    const cloudEur = monthly.eur * SERVICE_MONTHS_PER_YEAR;
    const costEur = cloudEur + yearlyAiEur + yearlyStripeEur;
    return {
      label: profile.label,
      costEur,
      revenueEur,
      profitEur: revenueEur - costEur,
      margin: revenueEur > 0 ? (revenueEur - costEur) / revenueEur : null,
      parts: monthly,
      aiEur: yearlyAiEur,
      stripeEur: yearlyStripeEur,
    };
  });
}
